import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { downloadPDF } from "../lib/pdf";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Search, Save, Info, RotateCcw, Loader2, ArrowLeft, Lock, Share2, Download } from "lucide-react";
import PerioChart from "../components/dental/PerioChart";
import OrthoTracker from "../components/dental/OrthoTracker";
import { startDentalCheckout } from "../lib/premium";

export const ToothIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 5.5c2-2.2 6-2.4 7.2.6 1 2.4-.4 5-.9 7.4-.4 2-.6 5.4-2 6.9-1.2 1.2-2-.6-2.3-2-.3-1.4-.6-3.4-2-3.4s-1.7 2-2 3.4c-.3 1.4-1.1 3.2-2.3 2-1.4-1.5-1.6-4.9-2-6.9-.5-2.4-1.9-5-.9-7.4C6 3.1 10 3.3 12 5.5Z" />
  </svg>
);

const CONDITIONS = [
  { key: "sano", label: "Sano / Borrar", color: "#ffffff", ring: "#CBD5C0" },
  { key: "caries", label: "Caries", color: "#EF4444" },
  { key: "obturacion", label: "Obturación", color: "#2563EB" },
  { key: "corona", label: "Corona", color: "#F59E0B" },
  { key: "endodoncia", label: "Endodoncia", color: "#8B5CF6" },
  { key: "sellador", label: "Sellador", color: "#10B981" },
  { key: "fracturado", label: "Fracturado", color: "#F97316" },
  { key: "ausente", label: "Ausente / Extraído", color: "#9CA3AF" },
  { key: "implante", label: "Implante", color: "#475569" },
  { key: "erupcionar", label: "Por erupcionar", color: "#BAE6FD", ring: "#38BDF8" },
];
const COND = Object.fromEntries(CONDITIONS.map((c) => [c.key, c]));
const WHOLE_ONLY = new Set(["ausente", "implante", "corona", "endodoncia", "erupcionar", "sano"]);

function toothSvgStr(num, d, x, y, S) {
  const col = (k) => (k ? COND[k]?.color || "#fff" : "#fff");
  let el = `<rect x="${x}" y="${y}" width="${S}" height="${S}" rx="4" fill="#fff" stroke="#CBD5C0" stroke-width="1.2"/>`;
  if (d?.c) {
    el += `<rect x="${x + 1}" y="${y + 1}" width="${S - 2}" height="${S - 2}" rx="3" fill="${COND[d.c].color}" opacity="${d.c === "erupcionar" ? 0.5 : 0.9}"/>`;
    if (d.c === "ausente") el += `<line x1="${x + 4}" y1="${y + 4}" x2="${x + S - 4}" y2="${y + S - 4}" stroke="#fff" stroke-width="2"/><line x1="${x + S - 4}" y1="${y + 4}" x2="${x + 4}" y2="${y + S - 4}" stroke="#fff" stroke-width="2"/>`;
  } else if (d?.s) {
    const s = d.s, a = S * 0.26, b = S * 0.48;
    el += `<rect x="${x}" y="${y}" width="${S}" height="${a}" fill="${col(s.V)}" stroke="#E5E7EB" stroke-width="0.5"/>`;
    el += `<rect x="${x}" y="${y + S - a}" width="${S}" height="${a}" fill="${col(s.L)}" stroke="#E5E7EB" stroke-width="0.5"/>`;
    el += `<rect x="${x}" y="${y + a}" width="${a}" height="${b}" fill="${col(s.M)}" stroke="#E5E7EB" stroke-width="0.5"/>`;
    el += `<rect x="${x + S - a}" y="${y + a}" width="${a}" height="${b}" fill="${col(s.D)}" stroke="#E5E7EB" stroke-width="0.5"/>`;
    el += `<rect x="${x + a}" y="${y + a}" width="${b}" height="${b}" fill="${col(s.O)}" stroke="#E5E7EB" stroke-width="0.5"/>`;
  }
  el += `<text x="${x + S / 2}" y="${y + S + 10}" font-size="10" font-family="Arial" font-weight="bold" fill="#6B7280" text-anchor="middle">${num}</text>`;
  return el;
}

function buildOdontogramSvg(rows, teeth) {
  const S = 34, gap = 8, rowH = 60, pad = 24;
  const maxLen = Math.max(...rows.map((r) => r.length));
  const W = pad * 2 + maxLen * (S + gap);
  const H = pad * 2 + rows.length * rowH;
  let el = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><rect width="${W}" height="${H}" fill="#ffffff"/>`;
  rows.forEach((row, ri) => {
    const rowW = row.length * (S + gap) - gap;
    const startX = (W - rowW) / 2;
    row.forEach((num, i) => { el += toothSvgStr(num, teeth[num] || {}, startX + i * (S + gap), pad + ri * rowH, S); });
  });
  return el + `</svg>`;
}

async function svgToPng(svgStr) {
  const url = URL.createObjectURL(new Blob([svgStr], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const scale = 2;
    const canvas = document.createElement("canvas");
    canvas.width = img.width * scale; canvas.height = img.height * scale;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale); ctx.drawImage(img, 0, 0);
    return canvas.toDataURL("image/png");
  } finally { URL.revokeObjectURL(url); }
}

const PERM = { ur: [18, 17, 16, 15, 14, 13, 12, 11], ul: [21, 22, 23, 24, 25, 26, 27, 28], lr: [48, 47, 46, 45, 44, 43, 42, 41], ll: [31, 32, 33, 34, 35, 36, 37, 38] };
const TEMP = { ur: [55, 54, 53, 52, 51], ul: [61, 62, 63, 64, 65], lr: [85, 84, 83, 82, 81], ll: [71, 72, 73, 74, 75] };

function rowsFor(dentition) {
  if (dentition === "temporal") return [[...TEMP.ur, ...TEMP.ul], [...TEMP.lr, ...TEMP.ll]];
  if (dentition === "mixta") return [[...PERM.ur, ...PERM.ul], [...TEMP.ur, ...TEMP.ul], [...TEMP.lr, ...TEMP.ll], [...PERM.lr, ...PERM.ll]];
  return [[...PERM.ur, ...PERM.ul], [...PERM.lr, ...PERM.ll]];
}

const S = 34;
const ZONES = {
  V: `0,0 ${S},0 ${S * 0.7},${S * 0.3} ${S * 0.3},${S * 0.3}`,
  L: `0,${S} ${S},${S} ${S * 0.7},${S * 0.7} ${S * 0.3},${S * 0.7}`,
  M: `0,0 0,${S} ${S * 0.3},${S * 0.7} ${S * 0.3},${S * 0.3}`,
  D: `${S},0 ${S},${S} ${S * 0.7},${S * 0.7} ${S * 0.7},${S * 0.3}`,
};
const CENTER = { x: S * 0.3, y: S * 0.3, w: S * 0.4 };

function Tooth({ num, data, expanded, onWhole, onSurface, onExpand }) {
  const c = data?.c;
  const s = data?.s || {};
  const fillFor = (k) => (k ? COND[k]?.color || "#fff" : "#fff");
  return (
    <div className="flex flex-col items-center gap-1 select-none" data-testid={`tooth-${num}`}>
      <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`} className="cursor-pointer"
        onClick={() => !expanded && onWhole(num)} onDoubleClick={() => onExpand(num)}>
        <rect x="0" y="0" width={S} height={S} rx="4" fill="#fff" stroke={c ? COND[c].ring || COND[c].color : "#CBD5C0"} strokeWidth="1.5" />
        {c ? (
          <>
            <rect x="1.5" y="1.5" width={S - 3} height={S - 3} rx="3" fill={COND[c].color} opacity={c === "erupcionar" ? 0.5 : 0.9} />
            {c === "ausente" && <><line x1="4" y1="4" x2={S - 4} y2={S - 4} stroke="#fff" strokeWidth="2.2" /><line x1={S - 4} y1="4" x2="4" y2={S - 4} stroke="#fff" strokeWidth="2.2" /></>}
            {c === "corona" && <circle cx={S / 2} cy={S / 2} r={S / 2.9} fill="none" stroke="#fff" strokeWidth="2" />}
            {c === "endodoncia" && <path d={`M${S / 2},5 L${S / 2},${S - 5}`} stroke="#fff" strokeWidth="2.2" />}
            {c === "implante" && <path d={`M${S / 2},6 L${S / 2},${S - 6} M${S * 0.35},10 L${S * 0.65},10 M${S * 0.37},14 L${S * 0.63},14`} stroke="#fff" strokeWidth="1.6" />}
          </>
        ) : (
          <g>
            {Object.entries(ZONES).map(([z, pts]) => (
              <polygon key={z} points={pts} fill={fillFor(s[z])} stroke="#E5E7EB" strokeWidth="0.6"
                onClick={(e) => { if (expanded) { e.stopPropagation(); onSurface(num, z); } }}
                className={expanded ? "cursor-pointer hover:opacity-70" : ""} />
            ))}
            <rect x={CENTER.x} y={CENTER.y} width={CENTER.w} height={CENTER.w} fill={fillFor(s.O)} stroke="#E5E7EB" strokeWidth="0.6"
              onClick={(e) => { if (expanded) { e.stopPropagation(); onSurface(num, "O"); } }}
              className={expanded ? "cursor-pointer hover:opacity-70" : ""} />
          </g>
        )}
        {expanded && <rect x="0.5" y="0.5" width={S - 1} height={S - 1} rx="4" fill="none" stroke="#C4623F" strokeWidth="2" strokeDasharray="3 2" />}
      </svg>
      <span className="text-[10px] font-bold text-brand-ink/60 tabular-nums">{num}</span>
    </div>
  );
}

function PatientPicker({ onPick }) {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/patients").then((r) => setList(r.data)).catch(() => {}); }, []);
  const filtered = list.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="max-w-lg">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="odo-patient-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar paciente…"
          className="w-full pl-9 pr-3 py-3 rounded-2xl border border-stone-200 outline-none focus:border-brand-sage" />
      </div>
      <div className="mt-3 space-y-2">
        {filtered.map((p) => (
          <button key={p.patient_id} data-testid={`odo-patient-${p.patient_id}`} onClick={() => onPick(p)}
            className="w-full text-left bg-white rounded-2xl border border-stone-100 p-3.5 hover:border-brand-sage transition-all">
            <p className="font-semibold text-brand-ink">{p.name}</p>
            <p className="text-xs text-muted-foreground">{p.age?.text}{p.record_number ? ` · ${p.record_number}` : ""}</p>
          </button>
        ))}
        {filtered.length === 0 && <p className="text-center text-sm text-muted-foreground py-8">Sin pacientes.</p>}
      </div>
    </div>
  );
}

function autoDentition(months) {
  if (months == null) return "permanente";
  if (months < 72) return "temporal";
  if (months < 156) return "mixta";
  return "permanente";
}

export default function Odontogram() {
  const { user } = useAuth();
  // DentPi (módulo odontológico) SOLO para quien tiene el plan odontológico; no visible en cuentas médicas.
  const entitled = !!user?.has_odontogram;
  const [sp, setSp] = useSearchParams();
  const [patient, setPatient] = useState(null);
  const [teeth, setTeeth] = useState({});
  const [dentition, setDentition] = useState("permanente");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState("caries");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(null);
  const [tab, setTab] = useState("odontograma");

  const loadPatient = async (p) => {
    setPatient(p); setLoading(true); setExpanded(null); setTab("odontograma");
    try {
      const r = await api.get(`/patients/${p.patient_id}/odontogram`);
      setTeeth(r.data.teeth || {});
      setNotes(r.data.notes || "");
      setDentition(r.data.dentition || autoDentition(p.age?.months));
      setDirty(false);
    } catch { toast.error("No se pudo cargar"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const pid = sp.get("patient");
    if (pid && !patient) api.get(`/patients/${pid}`).then((r) => loadPatient(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const applyWhole = (num) => {
    setTeeth((t) => {
      const cur = t[num] || {};
      if (active === "sano") { const n = { ...t }; delete n[num]; return n; }
      if (cur.c === active && !Object.keys(cur.s || {}).length) { const n = { ...t }; delete n[num]; return n; }
      return { ...t, [num]: { c: active } };
    });
    setDirty(true);
  };
  const applySurface = (num, zone) => {
    if (WHOLE_ONLY.has(active)) { applyWhole(num); return; }
    setTeeth((t) => {
      const cur = { ...(t[num] || {}) }; delete cur.c;
      const s = { ...(cur.s || {}) };
      if (s[zone] === active) delete s[zone]; else s[zone] = active;
      cur.s = s;
      if (!Object.keys(s).length) { const n = { ...t }; delete n[num]; return n; }
      return { ...t, [num]: cur };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/patients/${patient.patient_id}/odontogram`, { dentition, teeth, notes });
      toast.success("Odontograma guardado ✓"); setDirty(false);
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const shareOdo = async () => {
    setSharing(true);
    try {
      if (dirty) await api.put(`/patients/${patient.patient_id}/odontogram`, { dentition, teeth, notes });
      setDirty(false);
      const png = await svgToPng(buildOdontogramSvg(rowsFor(dentition), teeth));
      const r = await api.post(`/patients/${patient.patient_id}/odontogram/share`, { image: png, dentition, notes });
      setShared(r.data);
      toast.success("Odontograma adjuntado al expediente ✓");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo compartir"); }
    finally { setSharing(false); }
  };

  const rows = useMemo(() => rowsFor(dentition), [dentition]);
  const summary = useMemo(() => {
    const counts = {};
    Object.values(teeth).forEach((t) => {
      if (t.c) counts[t.c] = (counts[t.c] || 0) + 1;
      Object.values(t.s || {}).forEach((k) => { counts[k] = (counts[k] || 0) + 1; });
    });
    return counts;
  }, [teeth]);

  if (!entitled) {
    return (
      <div className="max-w-md mx-auto text-center py-20" data-testid="odontogram-locked">
        <div className="w-14 h-14 rounded-2xl bg-brand-sand/40 text-brand-terracotta grid place-items-center mx-auto mb-4"><Lock size={26} /></div>
        <h1 className="font-outfit text-2xl font-extrabold text-brand-ink">ClinDent no habilitado</h1>
        <p className="text-muted-foreground mt-2">Actívalo con el <b>plan Odontológico ($170/mes)</b>: incluye todo lo del Básico + odontograma, periodoncia y ortodoncia.</p>
        <button data-testid="odontogram-buy-dental" onClick={startDentalCheckout}
          className="mt-5 inline-flex items-center justify-center gap-2 text-white font-outfit font-semibold px-6 py-3 rounded-2xl bg-teal-500 hover:bg-teal-600 transition-all">
          Activar módulo Odontológico $170
        </button>
        <p className="text-xs text-muted-foreground mt-3">¿Eres administrador? También puedes activarlo por médico desde el Panel.</p>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="space-y-6" data-testid="odontogram-page">
        <header>
          <p className="text-brand-sage font-bold text-sm tracking-wide uppercase flex items-center gap-2"><ToothIcon size={16} /> ClinDent</p>
          <h1 className="font-outfit text-3xl sm:text-4xl font-extrabold text-brand-ink tracking-tight mt-1">ClinDent · Suite dental</h1>
          <p className="text-muted-foreground mt-1">Elige un paciente para ver y editar su odontograma, periodoncia y ortodoncia.</p>
        </header>
        <PatientPicker onPick={(p) => { setSp({ patient: p.patient_id }); loadPatient(p); }} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="odontogram-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button data-testid="odo-change-patient" onClick={() => { setPatient(null); setSp({}); }} className="text-sm text-brand-sage font-semibold flex items-center gap-1 mb-1"><ArrowLeft size={15} /> Cambiar paciente</button>
          <h1 className="font-outfit text-2xl font-extrabold text-brand-ink flex items-center gap-2"><ToothIcon size={22} className="text-brand-terracotta" /> {patient.name}</h1>
          <p className="text-sm text-muted-foreground">{patient.age?.text}</p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "odontograma" && (<>
          <button data-testid="odo-share-btn" onClick={shareOdo} disabled={sharing}
            className="flex items-center gap-2 font-outfit font-semibold px-4 py-2.5 rounded-2xl bg-white border border-stone-200 text-brand-ink hover:border-brand-sage transition-all disabled:opacity-60">
            {sharing ? <Loader2 size={17} className="animate-spin" /> : <Share2 size={17} />} Compartir / Imprimir
          </button>
          <button data-testid="odo-save-btn" onClick={save} disabled={saving || !dirty}
            className={`flex items-center gap-2 font-outfit font-semibold px-5 py-2.5 rounded-2xl transition-all ${dirty ? "bg-brand-terracotta text-white hover:bg-brand-terracottaDark" : "bg-stone-100 text-brand-ink/40"}`}>
            {saving ? <Loader2 size={17} className="animate-spin" /> : <Save size={17} />} {dirty ? "Guardar" : "Guardado"}
          </button>
          </>)}
        </div>
      </div>

      {/* Pestañas del módulo dental */}
      <div className="flex gap-1.5 border-b border-stone-200" data-testid="dental-tabs">
        {[["odontograma", "Odontograma"], ["periodoncia", "Periodoncia"], ["ortodoncia", "Ortodoncia"]].map(([k, l]) => (
          <button key={k} data-testid={`dental-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-outfit font-semibold rounded-t-xl -mb-px border-b-2 transition-all ${tab === k ? "border-brand-terracotta text-brand-terracotta" : "border-transparent text-brand-ink/50 hover:text-brand-ink"}`}>{l}</button>
        ))}
      </div>

      {tab === "periodoncia" && <PerioChart patient={patient} />}
      {tab === "ortodoncia" && <OrthoTracker patient={patient} />}

      {tab === "odontograma" && (<>
      <Dialog open={!!shared} onOpenChange={(o) => !o && setShared(null)}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="odo-share-dialog">
          <DialogHeader><DialogTitle className="font-outfit text-brand-ink">Odontograma listo</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Odontograma adjuntado al expediente y listo para compartir por QR.</DialogDescription>
          <div className="text-center">
            {shared && (
              <div className="inline-block bg-white p-3 rounded-2xl border-2 border-brand-terracotta/20">
                <QRCodeCanvas value={`${window.location.origin}/receta/${shared.share_id}`} size={160} fgColor="#C4623F" level="M" />
              </div>
            )}
            <p className="text-sm text-muted-foreground mt-3">Escanea el QR para verlo en el teléfono. Ya quedó guardado como documento en el expediente.</p>
            <button data-testid="odo-download-pdf" onClick={() => downloadPDF(shared, `${window.location.origin}/receta/${shared.share_id}`)}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-brand-terracotta text-white font-outfit font-semibold py-3 rounded-2xl hover:bg-brand-terracottaDark transition-all">
              <Download size={17} /> Descargar / Imprimir PDF
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dentición */}
      <div className="flex gap-2" data-testid="dentition-toggle">
        {[["temporal", "Temporal"], ["mixta", "Mixta"], ["permanente", "Permanente"]].map(([k, l]) => (
          <button key={k} data-testid={`dentition-${k}`} onClick={() => { setDentition(k); setExpanded(null); }}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${dentition === k ? "bg-brand-ink text-white" : "bg-white border border-stone-200 text-brand-ink/70 hover:border-brand-ink/40"}`}>{l}</button>
        ))}
      </div>

      {/* Paleta de condiciones */}
      <div className="bg-white rounded-2xl border border-stone-100 p-3">
        <div className="flex items-center gap-1.5 text-[11px] text-brand-ink/50 mb-2"><Info size={12} /> Elige una condición, luego toca un diente. Doble clic en un diente = marcar caras.</div>
        <div className="flex flex-wrap gap-2" data-testid="condition-palette">
          {CONDITIONS.map((cnd) => (
            <button key={cnd.key} data-testid={`cond-${cnd.key}`} onClick={() => setActive(cnd.key)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all ${active === cnd.key ? "border-brand-ink" : "border-transparent bg-stone-50 hover:bg-stone-100"}`}>
              <span className="w-3.5 h-3.5 rounded" style={{ backgroundColor: cnd.color, border: `1px solid ${cnd.ring || cnd.color}` }} /> {cnd.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-brand-sage" size={28} /></div>
      ) : (
        <div className="bg-gradient-to-b from-brand-sand/20 to-white rounded-3xl border border-stone-100 p-4 sm:p-6 overflow-x-auto" data-testid="odontogram-chart">
          <div className="min-w-max mx-auto space-y-4">
            {rows.map((row, ri) => (
              <div key={ri} className={`flex justify-center gap-1.5 ${dentition === "mixta" && (ri === 1 || ri === 2) ? "opacity-95" : ""}`}>
                {row.map((num) => (
                  <Tooth key={num} num={num} data={teeth[num]} expanded={expanded === num}
                    onWhole={applyWhole} onSurface={applySurface} onExpand={(n) => setExpanded(expanded === n ? null : n)} />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resumen + notas */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="odo-summary">
          <p className="font-outfit font-bold text-brand-ink text-sm mb-2">Resumen</p>
          {Object.keys(summary).length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin hallazgos registrados.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary).map(([k, n]) => (
                <span key={k} className="flex items-center gap-1.5 text-xs font-semibold bg-stone-50 rounded-full px-3 py-1">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COND[k]?.color }} /> {COND[k]?.label}: {n}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-stone-100 p-4">
          <p className="font-outfit font-bold text-brand-ink text-sm mb-2">Notas odontológicas</p>
          <textarea data-testid="odo-notes" value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }} rows={3}
            placeholder="Observaciones, plan de tratamiento…" className="w-full px-3 py-2 rounded-xl border border-stone-200 outline-none focus:border-brand-sage text-sm" />
        </div>
      </div>
      </>)}
    </div>
  );
}
